import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Wallet, IndianRupee, TrendingUp, CreditCard, Clock,
    CheckCircle, XCircle, Loader2, X, ArrowUpRight,
    ArrowDownRight, Banknote, Receipt, AlertCircle, Building2,
    PiggyBank, Send, Eye, ChevronDown, ChevronUp, RefreshCw,
    Info, Package
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, API } from '../App';

const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const StatusBadge = ({ status }) => {
    const configs = {
        pending: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, label: 'Pending' },
        processing: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Loader2, label: 'Processing' },
        completed: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: 'Completed' },
        failed: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Failed' },
        settled: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle, label: 'Settled' },
    };
    const cfg = configs[status] || configs.pending;
    const Icon = cfg.icon;
    return (
        <Badge variant="outline" className={cfg.color}>
            <Icon className={`w-3 h-3 mr-1 ${status === 'processing' ? 'animate-spin' : ''}`} />
            {cfg.label}
        </Badge>
    );
};

const VendorPayments = () => {
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [earnings, setEarnings] = useState(null);
    const [payouts, setPayouts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [showPayoutModal, setShowPayoutModal] = useState(false);
    const [payoutAmount, setPayoutAmount] = useState('');
    const [payoutNotes, setPayoutNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [expandedTx, setExpandedTx] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const headers = { Authorization: `Bearer ${token}` };

    const fetchEarnings = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/vendor/earnings`, { headers });
            setEarnings(res.data);
        } catch (e) { toast.error('Failed to load earnings'); }
    }, [token]);

    const fetchPayouts = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/vendor/payouts`, { headers });
            setPayouts(res.data);
        } catch (e) { toast.error('Failed to load payouts'); }
    }, [token]);

    const fetchTransactions = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/vendor/transactions`, { headers });
            setTransactions(res.data);
        } catch (e) { toast.error('Failed to load transactions'); }
    }, [token]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await Promise.all([fetchEarnings(), fetchPayouts(), fetchTransactions()]);
            setLoading(false);
        };
        if (token) load();
    }, [token]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await Promise.all([fetchEarnings(), fetchPayouts(), fetchTransactions()]);
        setRefreshing(false);
        toast.success('Data refreshed');
    };

    const handleRequestPayout = async () => {
        const amount = parseFloat(payoutAmount);
        if (!amount || amount <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }
        if (amount > (earnings?.available_balance || 0)) {
            toast.error(`Maximum available: ${formatCurrency(earnings?.available_balance)}`);
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/api/vendor/payouts/request`, {
                amount, notes: payoutNotes || null
            }, { headers });
            toast.success('Payout request submitted!');
            setShowPayoutModal(false);
            setPayoutAmount('');
            setPayoutNotes('');
            fetchEarnings();
            fetchPayouts();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to request payout');
        } finally {
            setSubmitting(false);
        }
    };

    const hasPendingPayout = payouts.some(p => p.status === 'pending' || p.status === 'processing');

    const tabs = [
        { id: 'overview', label: 'Overview', icon: Wallet },
        { id: 'transactions', label: 'Transactions', icon: Receipt },
        { id: 'payouts', label: 'Payout History', icon: Banknote },
    ];

    if (loading) {
        return (
            <DashboardLayout userRole="vendor">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole="vendor">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                            <Wallet className="w-8 h-8 text-primary" />
                            Payments & Earnings
                        </h1>
                        <p className="text-muted-foreground mt-1">Track your earnings, transactions, and payout history</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="gap-2">
                            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                        <Button
                            onClick={() => setShowPayoutModal(true)}
                            disabled={!earnings?.available_balance || earnings.available_balance <= 0 || hasPendingPayout || !earnings?.has_bank_details}
                            className="gap-2"
                        >
                            <Send className="w-4 h-4" />
                            Request Payout
                        </Button>
                    </div>
                </div>

                {/* Bank Details Warning */}
                {earnings && !earnings.has_bank_details && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-amber-800 text-sm">Bank Details Required</p>
                            <p className="text-sm text-amber-700 mt-1">
                                Please add your bank details in your Profile → Payment tab before requesting a payout.
                            </p>
                        </div>
                    </div>
                )}

                {/* Pending Payout Notice */}
                {hasPendingPayout && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-blue-800 text-sm">Payout In Progress</p>
                            <p className="text-sm text-blue-700 mt-1">
                                You have a payout request that is being processed. New payout requests can be made after it's completed.
                            </p>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex gap-1 p-1 bg-muted/50 rounded-xl overflow-x-auto">
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${isActive
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* OVERVIEW TAB */}
                {activeTab === 'overview' && earnings && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                        {/* Earnings Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { label: 'Total Sales', value: formatCurrency(earnings.completed_sell_amount), icon: IndianRupee, color: 'from-emerald-500 to-green-600', desc: `${earnings.completed_orders} completed orders` },
                                { label: 'Platform Margin', value: formatCurrency(earnings.platform_margin), icon: PiggyBank, color: 'from-violet-500 to-purple-600', desc: `${earnings.commission_rate}% commission` },
                                { label: 'Your Earnings', value: formatCurrency(earnings.vendor_earnings), icon: TrendingUp, color: 'from-blue-500 to-indigo-600', desc: 'Net earnings' },
                                { label: 'Available Balance', value: formatCurrency(earnings.available_balance), icon: Wallet, color: 'from-emerald-500 to-green-600', desc: 'Withdrawable now' },
                            ].map((stat, i) => (
                                <motion.div
                                    key={stat.label}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    className="relative overflow-hidden rounded-xl border bg-card"
                                >
                                    <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                                    <div className="relative p-5">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-medium text-muted-foreground">{stat.label}</span>
                                            <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.color}`}>
                                                <stat.icon className="w-4 h-4 text-white" />
                                            </div>
                                        </div>
                                        <p className="text-2xl font-bold">{stat.value}</p>
                                        <p className="text-xs text-muted-foreground mt-1">{stat.desc}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Additional Details */}
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Financial Breakdown */}
                            <div className="bg-card rounded-xl border p-6 space-y-4">
                                <h3 className="font-semibold text-lg flex items-center gap-2">
                                    <Receipt className="w-5 h-5 text-primary" />
                                    Financial Breakdown
                                </h3>
                                <div className="space-y-3">
                                    {[
                                        { label: 'Total Sales (Completed)', value: formatCurrency(earnings.completed_sell_amount), color: 'text-foreground' },
                                        { label: `Platform Margin (${earnings.commission_rate}%)`, value: `-${formatCurrency(earnings.platform_margin)}`, color: 'text-violet-600' },
                                        { label: 'Net Earnings', value: formatCurrency(earnings.vendor_earnings), color: 'text-green-600', bold: true },
                                        { label: `In Hold Period (${earnings.settlement_hold_days} Days)`, value: `-${formatCurrency(earnings.holding_amount)}`, color: 'text-orange-500' },
                                        { label: 'Already Paid Out', value: `-${formatCurrency(earnings.total_paid_out)}`, color: 'text-blue-500' },
                                        { label: 'In Processing', value: `-${formatCurrency(earnings.processing_amount)}`, color: 'text-amber-500' },
                                    ].map((row) => (
                                        <div key={row.label} className="flex items-center justify-between py-1">
                                            <span className="text-sm text-muted-foreground">{row.label}</span>
                                            <span className={`text-sm font-mono ${row.bold ? 'font-bold' : 'font-medium'} ${row.color}`}>{row.value}</span>
                                        </div>
                                    ))}
                                    <div className="border-t pt-3 flex items-center justify-between">
                                        <span className="font-semibold text-emerald-700">Available to Withdraw</span>
                                        <span className="text-xl font-bold text-emerald-600 font-mono">{formatCurrency(earnings.available_balance)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Pending & Bank Details */}
                            <div className="space-y-4">
                                {/* Pending Balance */}
                                <div className="bg-card rounded-xl border p-6">
                                    <h3 className="font-semibold flex items-center gap-2 mb-3">
                                        <Clock className="w-5 h-5 text-amber-500" />
                                        Pending Balance
                                    </h3>
                                    <p className="text-2xl font-bold text-amber-600 font-mono">{formatCurrency(earnings.pending_vendor_earnings)}</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        From {earnings.pending_orders} order{earnings.pending_orders !== 1 ? 's' : ''} not yet completed.
                                        This amount will become available once orders are marked as completed.
                                    </p>
                                </div>

                                {/* Bank Account */}
                                <div className="bg-card rounded-xl border p-6">
                                    <h3 className="font-semibold flex items-center gap-2 mb-3">
                                        <Building2 className="w-5 h-5 text-primary" />
                                        Settlement Account
                                    </h3>
                                    {earnings.bank_details_preview ? (
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Bank</span>
                                                <span className="font-medium">{earnings.bank_details_preview.bank_name}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Account</span>
                                                <span className="font-mono">****{earnings.bank_details_preview.account_last4}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">IFSC</span>
                                                <span className="font-mono">{earnings.bank_details_preview.ifsc_code}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Holder</span>
                                                <span className="font-medium">{earnings.bank_details_preview.account_holder}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">
                                            No bank details added. Go to Profile → Payment to add your bank account.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* TRANSACTIONS TAB */}
                {activeTab === 'transactions' && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                        {transactions.length === 0 ? (
                            <div className="bg-card rounded-xl border p-12 text-center">
                                <Receipt className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                                <h3 className="text-lg font-medium text-muted-foreground">No Transactions Yet</h3>
                                <p className="text-sm text-muted-foreground/70 mt-1">
                                    Transactions will appear here once your orders are marked as completed.
                                </p>
                            </div>
                        ) : (
                            <div className="bg-card rounded-xl border overflow-hidden">
                                <div className="p-4 border-b flex items-center justify-between">
                                    <h3 className="font-semibold flex items-center gap-2">
                                        <Receipt className="w-5 h-5 text-primary" />
                                        Transaction Log
                                    </h3>
                                    <span className="text-sm text-muted-foreground">{transactions.length} transaction{transactions.length !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="divide-y">
                                    {transactions.map((tx) => (
                                        <div key={tx.order_id}>
                                            <button
                                                onClick={() => setExpandedTx(expandedTx === tx.order_id ? null : tx.order_id)}
                                                className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors text-left"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                                        <ArrowDownRight className="w-4 h-4 text-green-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-sm">Order #{tx.order_short_id}</p>
                                                        <p className="text-xs text-muted-foreground">{formatDate(tx.date)} • {tx.customer_name}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="text-right">
                                                        <p className="font-semibold text-sm text-green-600 font-mono">+{formatCurrency(tx.vendor_earning)}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {formatCurrency(tx.sell_amount)} sale • {formatCurrency(tx.platform_margin)} margin
                                                        </p>
                                                    </div>
                                                    {expandedTx === tx.order_id ? (
                                                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                                    ) : (
                                                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                                    )}
                                                </div>
                                            </button>
                                            <AnimatePresence>
                                                {expandedTx === tx.order_id && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: 'auto', opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        className="overflow-hidden"
                                                    >
                                                        <div className="px-4 pb-4 ml-12">
                                                            <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                                                                <p className="text-xs font-semibold text-muted-foreground uppercase">Items</p>
                                                                {tx.items.map((item, i) => (
                                                                    <div key={i} className="flex items-center justify-between text-sm">
                                                                        <div className="flex items-center gap-2">
                                                                            <Package className="w-3.5 h-3.5 text-muted-foreground" />
                                                                            <span>{item.name}</span>
                                                                            <span className="text-muted-foreground">×{item.qty}</span>
                                                                        </div>
                                                                        <div className="text-right text-xs">
                                                                            <span className="text-muted-foreground">Your: {formatCurrency(item.your_price)}</span>
                                                                            <span className="ml-2 font-mono">{formatCurrency(item.your_price * item.qty)}</span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                                <div className="border-t pt-2 space-y-1">
                                                                    <div className="flex justify-between text-sm">
                                                                        <span className="text-muted-foreground">Total Sale (Sell Price)</span>
                                                                        <span className="font-mono">{formatCurrency(tx.sell_amount)}</span>
                                                                    </div>
                                                                    <div className="flex justify-between text-sm">
                                                                        <span className="text-muted-foreground">Platform Margin</span>
                                                                        <span className="font-mono text-violet-600">{formatCurrency(tx.platform_margin)}</span>
                                                                    </div>
                                                                    <div className="flex justify-between text-sm font-semibold">
                                                                        <span>Your Earning</span>
                                                                        <span className="text-green-600 font-mono">{formatCurrency(tx.vendor_earning)}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}

                {/* PAYOUTS TAB */}
                {activeTab === 'payouts' && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                        {payouts.length === 0 ? (
                            <div className="bg-card rounded-xl border p-12 text-center">
                                <Banknote className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                                <h3 className="text-lg font-medium text-muted-foreground">No Payouts Yet</h3>
                                <p className="text-sm text-muted-foreground/70 mt-1">
                                    Your payout history will appear here once you request your first payout.
                                </p>
                                {earnings?.available_balance > 0 && (
                                    <Button onClick={() => setShowPayoutModal(true)} className="mt-4 gap-2">
                                        <Send className="w-4 h-4" />
                                        Request Your First Payout
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="bg-card rounded-xl border overflow-hidden">
                                <div className="p-4 border-b">
                                    <h3 className="font-semibold flex items-center gap-2">
                                        <Banknote className="w-5 h-5 text-primary" />
                                        Payout History
                                    </h3>
                                </div>
                                <div className="divide-y">
                                    {payouts.map((payout) => (
                                        <div key={payout.id} className="p-4 hover:bg-muted/20 transition-colors">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${payout.status === 'completed' ? 'bg-green-100' :
                                                        payout.status === 'failed' ? 'bg-red-100' :
                                                            payout.status === 'processing' ? 'bg-blue-100' : 'bg-amber-100'
                                                        }`}>
                                                        <ArrowUpRight className={`w-4 h-4 ${payout.status === 'completed' ? 'text-green-600' :
                                                            payout.status === 'failed' ? 'text-red-600' :
                                                                payout.status === 'processing' ? 'text-blue-600' : 'text-amber-600'
                                                            }`} />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-sm">Payout Request</p>
                                                        <p className="text-xs text-muted-foreground">{formatDate(payout.requested_at)}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="text-right">
                                                        <p className="font-semibold text-sm font-mono">{formatCurrency(payout.amount || payout.net_amount)}</p>
                                                        <StatusBadge status={payout.status} />
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Details Row */}
                                            <div className="ml-12 mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
                                                <span>Amount: {formatCurrency(payout.amount || payout.net_amount)}</span>
                                                {payout.payment_reference && (
                                                    <span className="font-mono text-primary">UTR: {payout.payment_reference}</span>
                                                )}
                                                {payout.admin_notes && (
                                                    <span className="italic">Note: {payout.admin_notes}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </motion.div>
                )}
            </div>

            {/* Request Payout Modal */}
            <AnimatePresence>
                {showPayoutModal && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                        onClick={() => setShowPayoutModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-card rounded-xl border shadow-2xl w-full max-w-md"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6 border-b flex items-center justify-between">
                                <h2 className="text-lg font-semibold flex items-center gap-2">
                                    <Send className="w-5 h-5 text-primary" />
                                    Request Payout
                                </h2>
                                <Button variant="ghost" size="icon" onClick={() => setShowPayoutModal(false)}>
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                            <div className="p-6 space-y-4">
                                {/* Available Balance */}
                                <div className="bg-gradient-to-br from-primary/5 to-violet-500/5 rounded-xl p-5 text-center border border-primary/10">
                                    <p className="text-sm text-muted-foreground">Available Balance</p>
                                    <p className="text-3xl font-bold text-primary font-mono mt-1">{formatCurrency(earnings?.available_balance)}</p>
                                </div>

                                {/* Amount Input */}
                                <div className="space-y-2">
                                    <Label>Payout Amount (₹)</Label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-bold">₹</span>
                                        <Input
                                            type="number"
                                            placeholder="0.00"
                                            value={payoutAmount}
                                            onChange={(e) => setPayoutAmount(e.target.value)}
                                            max={earnings?.available_balance}
                                            min={1}
                                            className="pl-8 text-lg font-mono"
                                        />
                                    </div>
                                    <button
                                        onClick={() => setPayoutAmount(String(earnings?.available_balance || 0))}
                                        className="text-xs text-primary hover:underline"
                                    >
                                        Withdraw full balance
                                    </button>
                                </div>

                                {/* Notes */}
                                <div className="space-y-2">
                                    <Label>Notes (optional)</Label>
                                    <Input
                                        placeholder="e.g. Monthly settlement"
                                        value={payoutNotes}
                                        onChange={(e) => setPayoutNotes(e.target.value)}
                                    />
                                </div>

                                {/* Bank Details Confirmation */}
                                {earnings?.bank_details_preview && (
                                    <div className="bg-muted/30 rounded-lg p-4 space-y-1 text-sm">
                                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Payout will be sent to</p>
                                        <p>{earnings.bank_details_preview.bank_name} • ****{earnings.bank_details_preview.account_last4}</p>
                                        <p className="text-muted-foreground">IFSC: {earnings.bank_details_preview.ifsc_code}</p>
                                    </div>
                                )}

                                {/* Info */}
                                <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700 flex items-start gap-2">
                                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span>Payouts are typically processed within 2-5 business days. You will receive the UTR/reference number once the transfer is complete.</span>
                                </div>
                            </div>
                            <div className="p-6 border-t flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setShowPayoutModal(false)}>Cancel</Button>
                                <Button onClick={handleRequestPayout} disabled={submitting} className="gap-2">
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    Submit Request
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </DashboardLayout>
    );
};

export default VendorPayments;
